<template>
  <div class="mission-check-in" :class="{ 'is-fixed': isFixed }">
    <div class="mission-check-in__box">
      <p class="mission-check-in__title">CHECK IN</p>
      <button
        class="mission-check-in__button"
        :class="`is-${checkInStatus}`"
        :disabled="isDisabled"
        @click="handleCheckInClicked"
      >
        <mission-check-in-icon :width="44" :height="44" v-if="checkInStatus !== 'checked'" />
        <mission-checked-in-icon v-else />
      </button>
      <div
        class="mission-check-in__message"
        :class="{ 'mission-check-in__message--active--for-pc': isPc && checkInStatus === 'active' }"
      >
        <p class="mission-check-in__message-check-in">
          {{ message }}
          <span
            v-if="isPc && checkInStatus === 'active'"
            class="mission-check-in__message-pc-caution"
            >{{ $t('MissionPage.checkIn.message.caution') }}</span
          >
        </p>
      </div>
      <button v-if="isFixed" class="mission-check-in__close" @click="handleCloseClicked">
        <span>Close</span>
        <span class="mission-check-in__close-icon">
          <mission-accordion-icon />
        </span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import I18n from '@/locales/I18n'
import MissionCheckInIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionCheckInIcon.vue'
import MissionAccordionIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionAccordionIcon.vue'
import MissionCheckedInIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionCheckedInIcon.vue'
import MissionProgressDocument from '@/store/stores/collectionModule/documents/mission/MissionProgressDocument'
import DeviceInfo from '@/util/DeviceInfo'

type CheckInStatusType = 'inactive' | 'active' | 'checked'

export default defineComponent({
  name: 'MissionCheckInSection',
  components: { MissionCheckedInIcon, MissionAccordionIcon, MissionCheckInIcon },
  props: {
    /**
     * 今シーズン：開催中のラウンドチェックインまたはイベントチェックインミッション一覧
     */
    checkInMissionsBeingHeldForThisSeason: {
      type: Array as PropType<MissionProgressDocument[]>,
      required: true,
    },
    successfulCheckIn: {
      type: Boolean,
    },
    isFixed: {
      type: Boolean,
    },
  },
  setup(_props, { emit }) {
    const isPc = DeviceInfo.isForPC()
    /**
     * チェックインの状態を管理する
     */
    const checkInStatus = computed(() => {
      let status = 'inactive' as CheckInStatusType
      if (_props.successfulCheckIn) {
        status = 'checked'
      } else if (_props.checkInMissionsBeingHeldForThisSeason.length > 0) {
        status = 'active'
      }
      return status
    })
    /**
     * チェックインボタン下部のメッセージの表示内容
     */
    const message = computed(() => {
      switch (checkInStatus.value) {
        case 'checked':
          return I18n.t('MissionPage.checkIn.message.checked')
        case 'active':
          return I18n.t('MissionPage.checkIn.message.active')
        default:
          return I18n.t('MissionPage.checkIn.message.inactive')
      }
    })
    /**
     * チェックインボタンを押せるかどうか
     */
    const isDisabled = computed(
      () => checkInStatus.value === 'inactive' || checkInStatus.value === 'checked',
    )
    /**
     * 閉じるボタンが押下されたときの処理
     */
    const handleCloseClicked = () => {
      emit('onClickClose')
    }
    /**
     * チェックインボタンが押下されたときの処理
     */
    const handleCheckInClicked = () => {
      emit('onClickCheckIn')
    }
    return {
      isPc,
      isDisabled,
      checkInStatus,
      message,
      handleCloseClicked,
      handleCheckInClicked,
    }
  },
})
</script>

<style scoped lang="scss">
.mission-check-in {
  text-align: center;

  &.is-fixed {
    .mission-check-in__box {
      padding: 16px 0 0;
      border-width: 0 0 1px;
      border-radius: 0;
      backdrop-filter: blur(60px);
    }
  }

  &__box {
    min-height: 180px;
    padding: 16px 16px 0;
    background-color: $white10;
    border: 1px solid rgba(#fff, 0.2);
    border-radius: 10px;
    backdrop-filter: blur(40px);
  }

  &__title {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    margin: 10px auto 0;
    background: linear-gradient(
      85deg,
      #00ffba -121.14%,
      #00ffba -121.14%,
      rgba(0, 255, 186, 0.5) -80.24%,
      #00ffba -34.6%,
      rgba(0, 255, 186, 0.5) 10.88%,
      #00ffba 51.93%,
      rgba(0, 255, 186, 0.5) 91.87%
    );
    border: 3px solid $black10;
    border-radius: 50%;
    box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.22), 0 0 20px 0 rgba(0, 219, 166, 0.4);

    &:disabled {
      color: $white70;
      background: $black10;
      border-color: transparent;
      box-shadow: none;
    }

    &.is-checked {
      color: $green-light;
      background: transparent;
      border: none;
      box-shadow: none;
    }
  }

  &__message {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    margin-top: 2px;
    font-family: $fontSignup;
    font-size: 12px;
    font-weight: 700;
    white-space: pre-wrap;

    &--active--for-pc {
      height: 60px;
    }

    &-check-in {
      width: 100%;
    }

    &-pc-caution {
      color: $white70;
      display: block;
      margin-top: 2px;
    }
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 46px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: $white;
  }

  &__close-icon {
    margin-left: 7px;
    transform: rotate(180deg);
  }
}
</style>
